import {Component, EventEmitter, Input, OnInit, Output, OnDestroy} from '@angular/core';
import CustomerPondModel from 'src/app/services/customerPond.model';
import {CustomerPondService} from 'src/app/services/customerPond.service';
import WaterReadingsModel from 'src/app/services/waterReadings.model';
import * as _ from 'lodash';
import {FarmSiteService} from 'src/app/services/farmSite.service';
import {PondSectionService} from 'src/app/services/pondSection.service';
import FarmSiteModel from 'src/app/services/farmSite.model';
import PondSectionModel from 'src/app/services/pondSection.model';
import {WaterReadingsService} from 'src/app/services/waterReadings.service';
import {ToastrService} from 'ngx-toastr';
import { CommonMethodsService } from 'src/app/services/commonMethods.service';

declare var $: any;

/**
 * @component BatchWaterQualityComponent
 * @selector app-batch-water-quality
 * @description Entry interface for adding new batch water quality readings to aquaculture farm sections.
 *
 * This component provides a user interface for creating and submitting new water quality reading batches
 * across multiple ponds/tanks within a farm section. It supports data entry with validation, preview mode,
 * bulk operations, and batch submission with custom parameter support.
 *
 * Key Features:
 * 1. Hierarchical Location Selection - Business → Farm Site → Section → Ponds with auto-selection
 * 2. Date/Time Batch Entry - Select date range and time for water quality readings batch
 * 3. Multi-Pond Water Parameters - Entry forms for 24+ water quality parameters per pond
 * 4. Custom Parameters Support - Store user-defined water parameters in custom fields
 * 5. Bulk Operations - Apply one pond's parameters to all selected ponds simultaneously
 * 6. Form State Management - Entry, Edit, Preview modes with button toggles
 * 7. Data Validation - Numeric input validation and required field checks
 * 8. Pond Management - Add/remove ponds from batch with confirmation
 * 9. Grouping by Parameters - Group ponds with identical readings for efficient storage
 * 10. Transaction Persistence - Submit validated batch data to API with success feedback
 *
 * Architecture:
 * - Hierarchical Data: Pond → Water Quality Parameters → Custom Parameters
 * - State Machine: Entry (showInput=true) → Preview (showInput=false, previewButton=false) → Submit
 * - Dropdown Formats: Dynamic population based on parent selection (Business→Site→Section)
 * - Data Grouping: Ponds with same parameters grouped together for storage optimization
 * - Validation: Empty/null parameter cleanup and at least one parameter per pond required
 *
 * Role-Based Behavior:
 * - Admin users can select any business and create batch readings
 * - Farmer users automatically see their business (from localStorage)
 * - All users have same batch creation capability for assigned sections
 *
 * Data Flow:
 * 1. Component initialization loads farm sites and auto-selects single values if available
 * 2. User selects: Business → Farm Site (auto) → Section (auto) → Ponds displayed
 * 3. User enters from/to dates and test time for batch identification
 * 4. User enters water quality parameters for each pond via form input fields
 * 5. Optional: Apply first pond's parameters to all ponds via applyToAllPonds()
 * 6. Preview mode validates all ponds have at least one parameter
 * 7. Data transformation groups ponds by matching parameters via batchWaterQualityReadingsSetPreparation()
 * 8. addBatchWaterQualityReadings() submits batch with validation checks and API call
 * 9. On success: Show confirmation toast and emit event to parent for list reload
 *
 * Water Parameters (24 standard + custom):
 * Standard: pH, salinity, alkalinity, turbidity, TAN, temperature, UAM, SG, DO2, conductivity,
 * TDS, nitrite, ORP, magnesium, calcium, plankton, BOD, CO2, phosphorus, H2S gas, iron,
 * potassium, DA, sodium
 * Custom: User-defined parameters stored in cust_added_params JSON field
 *
 * @implements OnInit, OnDestroy
 * @selector app-batch-water-quality
 * @templateUrl ./batch-water-quality.component.html
 * @styleUrls ./batch-water-quality.component.scss
 *
 * @service ToastrService - Display success/warning/error notifications to user
 * @service CustomerPondService - Fetch pond details and culture data for sections
 * @service FarmSiteService - Load farm sites filtered by business entity
 * @service PondSectionService - Retrieve sections filtered by farm site
 * @service WaterReadingsService - Submit batch water quality readings to API
 * @service CommonMethodsService - Handle error messages and common utility operations
 *
 * @example
 * // Component usage in parent component
 * <app-batch-water-quality
 *   [businessId]="selectedBusinessId"
 *   [siteId]="selectedSiteId"
 *   [sectionId]="selectedSectionId"
 *   [businessName]="businessName"
 *   (gotoWaterQualityList)="onBatchCreated($event)">
 * </app-batch-water-quality>
 *
 * Data Submission Example:
 * User selects: Business 'AquaTech' → Farm Site 'North Farm' → Section 'Pond Area A'
 *   ├─ Ponds loaded: [Pond-1, Pond-2, Pond-3]
 *   ├─ Date Range: 2026-01-15 to 2026-01-20
 *   ├─ Test Time: 14:30 (2:30 PM)
 *   ├─ Enters parameters: pH=7.2, DO2=5.5, Temperature=28.3
 *   ├─ Preview validates: At least 1 parameter per pond ✓
 *   ├─ Submission groups ponds by matching parameters:
 *   │  └─ Group 1: [Pond-1, Pond-2] with same parameters
 *   │  └─ Group 2: [Pond-3] with different parameters
 *   └─ API creates batch entry with 2 reading sets (one per group)
 */
@Component({
  selector: 'app-batch-water-quality',
  templateUrl: './batch-water-quality.component.html',
  styleUrls: ['./batch-water-quality.component.scss']
})
export class BatchWaterQualityComponent implements OnInit, OnDestroy {

  /**
   * @type {boolean}
   * @description Toggle visibility of input fields for water parameter entry
   * @default true
   * @side-effect Controls input field visibility in template; set by edit/preview mode transitions
   */
  public showInput: boolean;

  /**
   * @type {boolean}
   * @description Enable edit button to return from preview mode back to edit mode
   * @default false
   * @side-effect Enables editBatchWaterQualityReadings() when clicked; controls button visibility
   */
  public editButton: boolean;

  /**
   * @type {boolean}
   * @description Enable submit button after preview validation passes
   * @default false
   * @side-effect Enables addBatchWaterQualityReadings() when clicked
   */
  public submitButton: boolean;

  /**
   * @type {boolean}
   * @description Enable preview button to validate data before submission
   * @default true
   * @side-effect Enables priviewBatchWaterQualityReadings() when clicked; disabled after preview
   */
  public previewButton: boolean;

  /**
   * @type {boolean}
   * @description Show/hide loading spinner while fetching data from API
   * @default false
   * @side-effect Set during async operations (getPondsBySectionId, farm site/section loading)
   */
  public responseLoader: boolean;

  /**
   * @type {boolean}
   * @description Disable submit button during API submission to prevent duplicate submissions
   * @default false
   * @side-effect Set to true during addBatchWaterQualityReadings, reset on completion
   */
  public isCommitDisabled: boolean;

  /**
   * @type {number}
   * @description Index of currently selected/focused pond in dropdown array
   * @side-effect Used by template for tracking pond focus state
   */
  public pondDisplayIndex: number;

  /**
   * @type {boolean}
   * @description Toggle visibility of custom water parameter fields
   * @default true
   * @side-effect Controls display of additional custom parameter input fields
   */
  public showOrHideCustomParameters: boolean;

  /**
   * @type {any}
   * @description From date for batch water quality readings
   * @default new Date()
   * @side-effect Used in addBatchWaterQualityReadings for batch date range; formatted to YYYY-MM-DD
   */
  public from_date: any;

  /**
   * @type {any}
   * @description To date for batch water quality readings
   * @default new Date()
   * @side-effect Used in addBatchWaterQualityReadings for batch date range; formatted to YYYY-MM-DD
   */
  public to_date: any;

  /**
   * @type {string}
   * @description Not actively used; remnant property from earlier implementation
   * @deprecated Use test_time instead
   */
  public date_time: string;

  /**
   * @type {string}
   * @description Test time for batch readings in 12-hour format (e.g., "2:30 PM")
   * @default Current time formatted as "h:mm A"
   * @side-effect Combined with toDate in submission as "YYYY-MM-DD HH:mm" format
   */
  public test_time: string;

  /**
   * @type {string}
   * @description Logged-in user's unique identifier from localStorage
   * @default localStorage.getItem('loginPersonId')
   * @side-effect Used for tracking user who created the batch
   */
  public loginPersonId: string;

  /**
   * @type {Array<CustomerPondModel>}
   * @description All ponds in selected section with culture data
   * @side-effect Populated by getPondsBySectionId(); used to build pondsDetails_dropdown
   */
  public customerPonds: Array<CustomerPondModel>;

  /**
   * @type {Array<FarmSiteModel>}
   * @description Farm sites for selected business entity
   * @default []
   * @side-effect Populated by getAllFarmsitesBybusinessId or getAllFarmsitesByFarmerLoginBusinessID
   */
  public farmSites: Array<FarmSiteModel>;

  /**
   * @type {Array<PondSectionModel>}
   * @description Pond sections for selected farm site
   * @default []
   * @side-effect Populated by getSetionsByFarmsiteId; auto-selected if single section
   */
  public pondSections: Array<PondSectionModel>;

  /**
   * @type {WaterReadingsModel}
   * @description Model instance for single water reading transaction
   * @default new WaterReadingsModel()
   * @side-effect Used to prepare batch submission data
   */
  public waterReadingsModel: WaterReadingsModel;

  /**
   * @type {WaterReadingsModel}
   * @description Model instance for batch water readings transaction submission
   * @default new WaterReadingsModel()
   * @side-effect Populated with batch data (dates, time, readingSets) before API submission
   */
  public waterBatchTransaction: WaterReadingsModel;

  /**
   * @type {Array}
   * @default []
   * @description Dropdown array of pond details with water quality parameter forms
   * Each entry contains: pond_name, tank_id, pond_culture_id, water_type_id, soil_type_id,
   * isPreparatory, tank_number, waterQualityLogs (24+ parameters with initial null values)
   * @side-effect Modified by: getPondsBySectionId, onEditValueChange, applyToAllPonds, removePond
   */
  public pondsDetails_dropdown = new Array();

  /**
   * @type {string}
   * @Input
   * @description Business entity ID passed from parent component
   * Used to identify user's business context for authorization and data filtering
   */
  @Input('businessId') paramsbusinessId: string;

  /**
   * @type {string}
   * @Input
   * @description Farm site ID for which water quality readings are created
   * Identifies specific aquaculture farm location
   */
  @Input('siteId') paramsfarmsiteId: string;

  /**
   * @type {string}
   * @Input
   * @description Section/pond section ID within farm site
   * Used to filter and load pond details for data entry
   */
  @Input('sectionId') paramssectionId: string;

  /**
   * @type {string}
   * @Input
   * @description Business name displayed in UI header
   * Retrieved from parent component or localStorage
   */
  @Input('businessName') businessName: string;

  /**
   * @type {EventEmitter<any>}
   * @Output
   * @description Emit event to parent component when batch creation completes successfully
   * Parent should reload batch water quality list view after receiving this event
   * Emits: boolean true on successful batch submission
   */
  @Output() gotoWaterQualityList: EventEmitter<any> = new EventEmitter<any>();

  /**
   * @type {Date}
   * @default new Date()
   * @description Maximum date allowed in date picker (today)
   * Prevents future date selection in from_date and to_date pickers
   */
  maxDate: Date = new Date();

  /**
   * @method constructor
   * @description Initialize component with required service dependencies and model instances
   *
   * @service ToastrService - Notification service for displaying alerts to users
   * @service CustomerPondService - Service for loading pond and culture details from API
   * @service FarmSiteService - Service for retrieving farm sites by business entity
   * @service PondSectionService - Service for fetching pond sections by farm site
   * @service WaterReadingsService - Service for submitting batch water quality readings
   * @service CommonMethodsService - Service for common utility methods and error handling
   *
   * @param {ToastrService} toasterService - Injectable for displaying notifications
   * @param {CustomerPondService} customerPondService - Injectable for pond data operations
   * @param {FarmSiteService} farmSitesService - Injectable for farm site data operations
   * @param {PondSectionService} pondSectionService - Injectable for section data operations
   * @param {WaterReadingsService} waterReadingsService - Injectable for water readings submission
   * @param {CommonMethodsService} commonMethodsService - Injectable for common utility operations
   *
   * @side-effect
   * - Initializes two WaterReadingsModel instances (waterReadingsModel, waterBatchTransaction)
   * - Services are stored as class properties for use throughout component lifecycle
   */
  constructor(private toasterService: ToastrService, public customerPondService: CustomerPondService, public farmSitesService: FarmSiteService, public pondSectionService: PondSectionService, public waterReadingsService: WaterReadingsService,
    public commonMethodsService: CommonMethodsService) {
    this.waterReadingsModel = new WaterReadingsModel();
    this.waterBatchTransaction = new WaterReadingsModel();
  }

  /**
   * @method ngOnInit
   * @lifecycle angular
   * @description Initialize component state and load dropdown data based on input parameters
   *
   * Execution flow:
   * 1. Set default UI state: responseLoader=true, buttons configured, input visible
   * 2. Initialize date fields with current date (from_date, to_date)
   * 3. Initialize test_time with current time in 12-hour format using Intl.DateTimeFormat
   * 4. Retrieve loginPersonId from localStorage for tracking
   * 5. Initialize jQuery date picker for date range selection
   * 6. If sectionId provided: Load ponds for section via getPondsBySectionId()
   *
   * @returns {void}
   * @side-effect
   * - Resets all UI state flags and pagination arrays
   * - Initializes date and time values
   * - Triggers async pond loading if sectionId is provided
   * - Sets up jQuery date picker functionality
   */
  public ngOnInit(): void {
    this.responseLoader = true;
    this.isCommitDisabled = false;
    this.previewButton = true;
    this.submitButton = false;
    this.editButton = false;
    this.showInput = true;
    this.showOrHideCustomParameters = true;
    this.from_date = new Date();
    this.to_date = new Date();
    this.test_time = new Intl.DateTimeFormat('en-US', {hour: 'numeric', minute: 'numeric', hour12: true}).format(new Date());
    this.loginPersonId = localStorage.getItem('loginPersonId');
    this.initializedatePicker();
    if (this.paramssectionId) {
      this.getPondsBySectionId(this.paramssectionId);
    }
  }

  /**
   * @method ngOnDestroy
   * @lifecycle angular
   * @description Cancel all pending HTTP requests when component is destroyed
   *
   * Execution flow:
   * 1. Cancel ongoing requests in CustomerPondService
   * 2. Cancel ongoing requests in FarmSitesService
   * 3. Cancel ongoing requests in PondSectionService
   *
   * Prevents memory leaks and unnecessary API calls after component removal from DOM
   *
   * @returns {void}
   * @side-effect Aborts all in-flight HTTP requests from component services
   */
  public ngOnDestroy(): void {
    this.customerPondService.cancelOnGoingHttpRequests();
    this.farmSitesService.cancelOnGoingHttpRequests();
    this.pondSectionService.cancelOnGoingHttpRequests();
  }

  /**
   * @method getAllFarmsitesBybusinessId
   * @async
   * @description Load all farm sites for selected business entity (admin users)
   *
   * Execution flow:
   * 1. Clear current paramsfarmsiteId selection
   * 2. Fetch farm sites from FarmSiteService for specified business ID
   * 3. Set responseLoader=true during loading
   * 4. Call setSingleValueForFormLocation() to auto-select single farm site if available
   *
   * @returns {Promise<void>}
   * @side-effect
   * - Populates farmSites array with business farm sites
   * - Clears farm site ID selection (paramsfarmsiteId = '')
   * - Auto-selects single farm site if only one exists
   */
  public async getAllFarmsitesBybusinessId() {
    try {
      this.paramsfarmsiteId = '';
      this.farmSites = [];
      this.farmSites = await this.farmSitesService.getFarmeSitesByFarmId(this.paramsbusinessId);
      this.responseLoader = true;
      this.setSingleValueForFormLocation();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method getAllFarmsitesByFarmerLoginBusinessID
   * @async
   * @description Load all farm sites for logged-in farmer's business (farmer users)
   *
   * Execution flow:
   * 1. Clear current paramsfarmsiteId selection
   * 2. Fetch farm sites from localStorage.businessID (farmer's business entity)
   * 3. Set responseLoader=true during loading
   * 4. Call setSingleValueForFormLocation() to auto-select single farm site if available
   *
   * Uses farmer's business ID from localStorage instead of parameter businessId
   *
   * @returns {Promise<void>}
   * @side-effect
   * - Populates farmSites array with farmer's farm sites
   * - Clears farm site ID selection (paramsfarmsiteId = '')
   * - Auto-selects single farm site if only one exists
   */
  public async getAllFarmsitesByFarmerLoginBusinessID() {
    try {
      this.paramsfarmsiteId = '';
      this.farmSites = await this.farmSitesService.getFarmeSitesByFarmId(localStorage.businessID);
      this.responseLoader = true;
      this.setSingleValueForFormLocation();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method setSingleValueForFormLocation
   * @description Auto-select farm site if only one farm site exists for business entity
   *
   * Logic:
   * - If farmSites array has exactly one entry, automatically set paramsfarmsiteId to that site's ID
   * - Triggers getSetionsByFarmsiteId() to auto-load sections for auto-selected farm site
   * - Skips automatic selection if multiple or no farm sites exist
   *
   * Improves UX by eliminating unnecessary dropdown selections when only one option available
   *
   * @returns {void}
   * @side-effect
   * - Sets paramsfarmsiteId if single farm site available
   * - Triggers async section loading for auto-selected farm site
   */
  public setSingleValueForFormLocation() {
    if (this.farmSites && this.farmSites.length == 1) {
      this.paramsfarmsiteId = this.farmSites[0].id;
      this.getSetionsByFarmsiteId(this.paramsfarmsiteId);
    }
  }

  /**
   * @method getSetionsByFarmsiteId
   * @async
   * @description Load all pond sections for selected farm site
   *
   * Execution flow:
   * 1. Clear current paramssectionId selection
   * 2. Fetch sections from PondSectionService for specified farm site ID
   * 3. Call setSingleValueForSection() to auto-select single section if available
   *
   * @param {string} farmsiteid - Farm site ID to load sections for
   * @returns {Promise<void>}
   * @side-effect
   * - Populates pondSections array with farm site sections
   * - Clears section ID selection (paramssectionId = undefined)
   * - Auto-selects single section if only one exists
   */
  public async getSetionsByFarmsiteId(farmsiteid: string) {
    try {
      this.paramssectionId = undefined;
      this.pondSections = [];
      this.pondSections = await this.pondSectionService.getSectionsByFarmsiteId(farmsiteid);
      this.setSingleValueForSection();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method setSingleValueForSection
   * @description Auto-select section if only one section exists for farm site
   *
   * Logic:
   * - If pondSections array has exactly one entry, automatically set paramssectionId to that section's ID
   * - Triggers getPondsBySectionId() to auto-load ponds for auto-selected section
   * - Skips automatic selection if multiple or no sections exist
   *
   * Improves UX by eliminating unnecessary dropdown selections when only one option available
   *
   * @returns {void}
   * @side-effect
   * - Sets paramssectionId if single section available
   * - Triggers async pond loading for auto-selected section
   */
  public setSingleValueForSection() {
    if (this.pondSections && this.pondSections.length == 1) {
      this.paramssectionId = this.pondSections[0].id;
      this.getPondsBySectionId(this.paramssectionId);
    }
  }

  /**
   * @method getPondsBySectionId
   * @async
   * @description Load all ponds with active culture data for selected section
   *
   * Execution flow:
   * 1. Set responseLoader=true to show loading spinner
   * 2. Fetch all ponds for section from CustomerPondService
   * 3. Filter ponds: exclude preparatory ponds, include only those with active PondCultureData (no end_date)
   * 4. For each active pond culture, create dropdown entry with:
   *    - Pond metadata: pond_name, tank_id, pond_culture_id, water_type_id, soil_type_id
   *    - Pond config: isPreparatory, tank_number
   *    - Water parameters: waterQualityLogs object with 24 standard parameters initialized to null
   * 5. Set responseLoader=false to hide loading spinner
   *
   * Standard water parameters initialized: pH, salinity, alkalinity, turbidity, TAN, temperature,
   * UAM, SG, DO2, conductivity, TDS, nitrite, ORP, magnesium, calcium, plankton, BOD, CO2,
   * phosphorus, H2S gas, iron, potassium, DA, sodium
   *
   * @param {string} section_id - Section identifier to load ponds for
   * @returns {Promise<void>}
   * @side-effect
   * - Populates customerPonds with all ponds for section
   * - Populates pondsDetails_dropdown with formatted pond data for form entry
   * - Sets responseLoader during async operation
   */
  public async getPondsBySectionId(section_id: string) {
    try {
      const waterparameters = ['pH', 'salinity', 'alkalinity', 'turbidity', 'tan', 'temperature', 'uam', 'sg', 'do2', 'conductivity', 'tds', 'nitrite', 'orp', 'magnesium', 'calcium', 'plankton', 'bod', 'co2', 'phosphorus', 'h2sGas', 'iron', 'potassium', 'da', 'sodium'];
      this.responseLoader = true;
      this.customerPonds = await this.customerPondService.getAllPondsBySectionId(section_id);
      if (this.customerPonds && this.customerPonds.length > 0) {
        this.pondsDetails_dropdown = this.customerPonds.reduce((acc, eachrec) => {
          if (!eachrec.isPreparatory && eachrec.PondCultureData && eachrec.PondCultureData.length > 0) {
            eachrec.PondCultureData.forEach(pcd => {
              if (!pcd.end_date) {
                acc.push({
                  pond_name: eachrec.name,
                  tank_id: pcd.pond_id,
                  pond_culture_id: pcd.id,
                  water_type_id: pcd.water_type_id,
                  soil_type_id: pcd.soil_type_id,
                  isPreparatory: eachrec.isPreparatory,
                  tank_number: eachrec.pond_code,
                  waterQualityLogs: waterparameters.reduce((acc, param) => ({...acc, [param]: null}), {}),
                });
              }
            });
          }
          return acc;
        }, []);
      }
      this.responseLoader = false;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method initializedatePicker
   * @description Initialize jQuery date picker widgets for from_date and to_date input fields
   *
   * Configuration:
   * - Both pickers have maxDate=today (prevents future date selection)
   * - onSelect callback formats selected date to "MM-Mon-YYYY" format (e.g., "01-Jan-2026")
   * - End date picker has minDate constraint based on start date selection
   * - Start date picker has maxDate constraint based on end date selection
   * - Selected date is written back to input field (#startdate or #enddate)
   *
   * Date format conversion: "M/d/yyyy" (from jQuery) → "MM-Mon-YYYY" (custom format)
   *
   * @returns {void}
   * @side-effect
   * - Initializes jQuery date picker instances on #startdate and #enddate elements
   * - Sets up callbacks for date range validation and formatting
   * - Updates from_date and to_date inputs with formatted date strings
   */
  public initializedatePicker() {
    let strArray = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    let contract_dates = $('#startdate').datepicker({
      maxDate: new Date(),
      onSelect: function(selectedDate) {
        let parsedDate = (selectedDate.split('/')[1] + '-' + strArray[(parseInt(selectedDate.split('/')[0]) - 1)] + '-' + selectedDate.split('/')[2]);
        let option = this.id == 'startdate' ? 'minDate' : 'maxDate',
          instance = $(this).data('datepicker'),
          date = $.datepicker.parseDate(instance.settings.dateFormat || $.datepicker._defaults.dateFormat, selectedDate, instance.settings);
        contract_dates.not(this).datepicker('option', option, date);
        $('#startdate').val(parsedDate);
      }
    });

    let end_dates = $('#enddate').datepicker({
      maxDate: new Date(),
      onSelect: function(selectedDate) {
        let parsedDate = (selectedDate.split('/')[1] + '-' + strArray[(parseInt(selectedDate.split('/')[0]) - 1)] + '-' + selectedDate.split('/')[2]);
        let option = this.id == 'startdate' ? 'minDate' : 'maxDate',
          instance = $(this).data('datepicker'),
          date = $.datepicker.parseDate(instance.settings.dateFormat || $.datepicker._defaults.dateFormat, selectedDate, instance.settings);
        end_dates.not(this).datepicker('option', option, date);
        $('#enddate').val(parsedDate);
      }
    });
  }

  /**
   * @method validateNumber
   * @description Validate numeric input for water quality parameter fields
   *
   * Validation rules:
   * 1. Allow: digits 0-9, decimal point (.), Backspace
   * 2. Prevent: Letters, special characters except above
   * 3. Prevent: Multiple dots in single number (only one decimal allowed)
   * 4. Prevent: Dot as first character (must start with number)
   *
   * Use case: Restricts input in water parameter fields to valid numeric values with optional decimal
   *
   * @param {any} event - Keyboard event from input field
   * @returns {void}
   * @side-effect Calls event.preventDefault() to block invalid keystrokes
   */
  public validateNumber(event: any) {
    const key = event.key;
    const value = event.target.value;
    // Allow numbers, the dot (decimal point), and the Backspace key
    if (
      !/^\d$/.test(key) && // Check if the entered character is a number
      key !== '.' && // Check if the entered character is a dot (decimal point)
      key !== 'Backspace' // Check if the Backspace key was pressed
    ) {
      event.preventDefault();
    }

    // Allow only one dot (decimal point)
    if (key === '.' && value.includes('.')) {
      event.preventDefault();
    }

    // Disallow the first letter being a dot
    if (key === '.' && value === '') {
      event.preventDefault();
    }
  }

  /**
   * @method onEditValueChange
   * @description Update water quality parameter value for specific pond when user edits field
   *
   * Real-time binding of edited values to waterQualityLogs object for selected pond.
   * This method is called on every change event from input fields, allowing instant updates
   * without form submission.
   *
   * @param {number} pondIndex - Array index of pond in pondsDetails_dropdown
   * @param {string} property - Name of water quality parameter being edited (e.g., "pH", "temperature")
   * @param {any} value - New value entered by user for the water quality parameter
   * @returns {void}
   * @side-effect Updates specific property in pondsDetails_dropdown[pondIndex].waterQualityLogs
   */
  public onEditValueChange(pondIndex: number, property: string, value: any) {
    // Update the specific pond's property with the edited value
    this.pondsDetails_dropdown[pondIndex].waterQualityLogs[property] = value;
  }

  /**
   * @method applyToAllPonds
   * @description Apply first pond's water quality parameters to all other ponds
   *
   * Execution flow:
   * 1. Validate that first pond has at least one water quality parameter via pondsSelectionValidation()
   * 2. If validation passes:
   *    - Extract edited values from first pond's waterQualityLogs
   *    - Merge these values into each other pond's waterQualityLogs (spread operator preserves existing values)
   * 3. If validation fails: Show error toast and return false
   *
   * Useful for bulk entry when multiple ponds have identical water quality conditions.
   * Spreads first pond's parameters over others without clearing their existing data.
   *
   * @returns {boolean|void} Returns false on validation failure
   * @side-effect
   * - Updates waterQualityLogs for all ponds except first (preserves first pond)
   * - Shows error toast if first pond has no parameters
   */
  public applyToAllPonds() {
    try {
      const filteredPonds = this.pondsSelectionValidation();
      if (filteredPonds.length > 0) {
        // Get the edited values for the specific pond
        const editedPondValues = this.pondsDetails_dropdown[0].waterQualityLogs;
        // Update all similar properties for the pond with the edited values
        this.pondsDetails_dropdown.forEach((pond, i) => {
          if (i !== 0) {
            pond.waterQualityLogs = {...pond.waterQualityLogs, ...editedPondValues};
          }
        });
      } else {
        this.toasterService.error('Readings data is not provided for any of the pond');
        return false;
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method priviewBatchWaterQualityReadings
   * @description Transition from entry mode to preview mode and validate data completeness
   *
   * Execution flow:
   * 1. Call pondsSelectionValidation() to check that at least one pond has water parameters
   * 2. If validation passes:
   *    - Hide input fields (showInput=false)
   *    - Hide preview button (previewButton=false)
   *    - Enable edit and submit buttons (editButton=true, submitButton=true)
   *    - Enable submit functionality (isCommitDisabled=false)
   * 3. If validation fails: Show error toast and return false
   *
   * UI state transition: Entry Mode → Preview Mode (read-only display of entered data)
   *
   * @returns {boolean|void} Returns false if validation fails
   * @side-effect Updates UI state flags to show preview mode controls
   */
  public priviewBatchWaterQualityReadings() {
    const filteredPonds = this.pondsSelectionValidation();
    if (filteredPonds.length > 0) {
      this.previewButton = false;
      this.editButton = true;
      this.submitButton = true;
      this.isCommitDisabled = false;
      this.showInput = false;
    } else {
      this.toasterService.error('Readings data is not provided for any of the pond');
      return false;
    }
  }

  /**
   * @method pondsSelectionValidation
   * @description Validate that ponds have water quality parameters entered and return filtered list
   *
   * Validation logic:
   * 1. Iterate through pondsDetails_dropdown array
   * 2. For each pond, clean waterQualityLogs object by removing null/empty string values
   * 3. Filter to return only ponds with at least one valid water parameter
   * 4. Returns empty array if no valid parameters found in any pond
   *
   * Used to ensure no empty water quality records are submitted to API
   *
   * @returns {Array} Array of ponds with valid water quality parameters
   * @side-effect None (does not modify component state, only returns filtered array)
   */
  public pondsSelectionValidation() {
    try {
      const filteredPonds = this.pondsDetails_dropdown.map((item) => {
        const waterQualityLogs = {...item.waterQualityLogs};
        Object.keys(waterQualityLogs).forEach((key) => {
          if (waterQualityLogs[key] === '' || waterQualityLogs[key] === null) {
            delete waterQualityLogs[key];
          }
        });
        return {...item, waterQualityLogs};
      }).filter((item) => Object.keys(item.waterQualityLogs).length > 0);
      return filteredPonds;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method editBatchWaterQualityReadings
   * @description Return from preview mode back to edit mode for parameter modifications
   *
   * UI state transition: Preview Mode → Edit Mode
   * - Shows input fields for editing (showInput=true)
   * - Hides edit and submit buttons (editButton=false, submitButton=false)
   * - Shows preview button to re-validate before submission (previewButton=true)
   *
   * @returns {void}
   * @side-effect Updates UI state flags to enable edit mode
   */
  public editBatchWaterQualityReadings() {
    this.editButton = false;
    this.submitButton = false;
    this.previewButton = true;
    this.showInput = true;
  }

  /**
   * @method batchWaterQualityReadingsSetPreparation
   * @description Transform entered water quality readings into API submission request body format
   *
   * Execution flow:
   * 1. Get validated ponds from pondsSelectionValidation()
   * 2. Group ponds by matching waterQualityLogs values (JSON.stringify as key)
   * 3. For each unique parameter set:
   *    - Extract custom parameters (magnesium, calcium, plankton, BOD, CO2, phosphorus, H2S, iron, potassium, DA, sodium)
   *    - Remove custom parameters from main object
   *    - Store custom parameters in cust_added_params JSON field
   * 4. Collect all tank IDs with same parameters into ponds array
   * 5. Return array of reading sets ready for API submission
   *
   * Data optimization: Ponds with identical water parameters are grouped together,
   * reducing storage space and API request size.
   *
   * @returns {Array} Array of reading set objects with grouped ponds and parameters
   * @side-effect None (does not modify component state)
   */
  public batchWaterQualityReadingsSetPreparation() {
    try {
      const groupedObjects = this.pondsSelectionValidation().reduce((acc: any, obj: any) => {
        let waterQualityLogsKey = JSON.stringify(obj.waterQualityLogs);
        if (!acc.hasOwnProperty(waterQualityLogsKey)) {
          acc[waterQualityLogsKey] = {...obj.waterQualityLogs, ponds: []};
        }

        // Define static custom parameter names"
        const customParameterNames = ['magnesium', 'calcium', 'plankton', 'bod', 'co2', 'phosphorus', 'h2sGas', 'iron', 'potassium', 'da', 'sodium'];

        // Filter the custom parameters from the input data
        const customParams = Object.keys(obj.waterQualityLogs)
          .filter(key => customParameterNames.includes(key))
          .map(key => ({key: `${key}`, value: obj.waterQualityLogs[key]}));

        // Remove the custom parameters from the top-level object
        Object.keys(acc[waterQualityLogsKey]).forEach((paramName: string) => {
          if (customParameterNames.includes(paramName)) {
            delete acc[waterQualityLogsKey][paramName];
          }
        });

        const {tank_id, pond_culture_id, water_type_id, soil_type_id, isPreparatory, tank_number} = obj;
        acc[waterQualityLogsKey].ponds.push({tank_id, pond_culture_id, water_type_id, soil_type_id, isPreparatory, tank_number});
        acc[waterQualityLogsKey].cust_added_params = JSON.stringify(customParams);
        return acc;
      }, {});
      return Object.values(groupedObjects);
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method formatDateToYMD
   * @description Convert Date object to YYYY-MM-DD string format for API submission
   *
   * Format: "YYYY-MM-DD" (e.g., "2026-01-19")
   * - Extracts year, month (zero-padded), and day (zero-padded)
   * - Used for batch date range fields (fromDate, toDate) in API requests
   *
   * @param {any} date - Date object to convert
   * @returns {string} Formatted date string in YYYY-MM-DD format
   */
  formatDateToYMD = (date: any): string => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  /**
   * @method onTimeSelected
   * @description Store selected time from time picker in 24-hour format
   *
   * Called when user selects time via time picker component.
   * Stores time in HH:mm format (e.g., '14:30') for later submission.
   *
   * @param {string} time - Selected time in HH:mm format
   * @returns {void}
   * @side-effect Updates test_time property with selected time
   */
  onTimeSelected(time: string): void {
    this.test_time = time;
  }

  /**
   * @method addBatchWaterQualityReadings
   * @async
   * @description Validate form data and submit batch water quality readings to API
   *
   * Validation checks (in order):
   * 1. Business ID selected
   * 2. Farm Site ID selected
   * 3. Section ID selected
   * 4. From Date selected
   * 5. To Date selected
   * 6. Test Time selected
   *
   * Execution flow (if all validations pass):
   * 1. Set isCommitDisabled=true to prevent duplicate submissions
   * 2. Format dates from Date objects to "YYYY-MM-DD" format
   * 3. Combine date and time: "YYYY-MM-DD HH:mm"
   * 4. Build waterBatchTransaction object with:
   *    - action='add', farm_site_id, section_id, batch_type='Water'
   *    - date range (fromDate, toDate, combined time)
   *    - Static device UUIDs (for tracking in external system)
   *    - readingSets from batchWaterQualityReadingsSetPreparation()
   * 5. Submit via waterReadingsService.addBatchWaterReadings()
   * 6. On success: Show success toast, emit event to parent, navigate to list
   * 7. On error: Show error toast, re-enable submit button
   *
   * @returns {boolean|Promise<void>}
   * @side-effect
   * - Submits API request to create batch water quality readings
   * - Emits gotoWaterQualityList event to parent component on success
   * - Shows toast notifications for user feedback
   * - Sets isCommitDisabled during submission
   */
  public async addBatchWaterQualityReadings() {
    if (!this.paramsbusinessId) {
      this.toasterService.error('Please select farm');
      return false;
    } else if (!this.paramsfarmsiteId) {
      this.toasterService.error('Please select farmsite');
      return false;
    } else if (!this.paramssectionId) {
      this.toasterService.error('Please select section');
      return false;
    } else if (!this.from_date) {
      this.toasterService.error('Please select from date');
      return false;
    } else if (!this.to_date) {
      this.toasterService.error('Please select to date');
      return false;
    } else if (!this.test_time) {
      this.toasterService.error('Please select test time');
      return false;
    } else {
      try {
        this.isCommitDisabled = true;
        const formattedFromDate = this.formatDateToYMD(this.from_date);
        const formattedToDate = this.formatDateToYMD(this.to_date);
        const formattedTime = this.test_time;
        this.waterBatchTransaction.to = 'add';
        this.waterBatchTransaction.farm_site_id = this.paramsfarmsiteId;
        this.waterBatchTransaction.section_id = Number(this.paramssectionId);
        this.waterBatchTransaction.batch_type = 'Water';
        this.waterBatchTransaction.fromDate = formattedFromDate;
        this.waterBatchTransaction.toDate = formattedToDate;
        this.waterBatchTransaction.time = this.waterBatchTransaction.toDate + ' ' + formattedTime;
        this.waterBatchTransaction.readingSets = this.batchWaterQualityReadingsSetPreparation();
        const batchReadingsAdded = await this.waterReadingsService.addBatchWaterReadings(this.waterBatchTransaction);
        if (batchReadingsAdded.readings.length) {
          this.toasterService.success('Batch Water Readings added successfully', '', {
            timeOut: 3000
          });
          this.gotoWaterQualityList.emit(true);
        } else {
          this.toasterService.error('Batch Water Readings added failed, Please try again');
          this.isCommitDisabled = false;
        }
      } catch (error) {
        this.commonMethodsService.userFriendlyAlertMessages(error);
        this.isCommitDisabled = false;
      }
    }
  }

  /**
   * @method removePond
   * @description Delete selected pond card from batch entry form
   *
   * Logic:
   * - If only one pond remains: Show warning toast and prevent deletion
   * - If multiple ponds exist: Remove pond from pondsDetails_dropdown array and return true
   *
   * Prevents accidental batch submission with zero ponds (validation would fail anyway)
   *
   * @param {number} i - Array index of pond to remove from pondsDetails_dropdown
   * @returns {boolean}
   * @side-effect
   * - Removes pond from pondsDetails_dropdown if multiple ponds exist
   * - Shows warning toast if attempting to delete last pond
   */
  public removePond(i: number) {
    if (this.pondsDetails_dropdown.length == 1) {
      this.toasterService.warning('Can\'t delete the pond card when there is only one pond');
      return false;
    } else {
      this.pondsDetails_dropdown.splice(i, 1);
      return true;
    }
  }
}
